/**
 * ToolsIndexPage — the /tools hub. Six free calculators, grouped by what
 * they size (lifting & access, drying, interior, debris), with the app
 * lead-magnet band and per-route SEO. Public page, rendered in ToolPageShell.
 */
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ConstructionIcon,
  Layers,
  MoveVertical,
  Droplets,
  SquarePen,
  Trash2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import ToolPageShell, { DimensionDivider, Eyebrow } from './ToolPageShell';
import AppPromo from './AppPromo';
import { useSeo, TOOLS_ORIGIN } from './useSeo';

interface ToolCard {
  category: string;
  title: string;
  blurb: string;
  to: string;
  Icon: LucideIcon;
}

const TOOLS: ToolCard[] = [
  {
    category: 'Lifting & access',
    title: 'Crane Cost Estimator',
    blurb:
      'Size a mobile crane from the pick weight, radius, and height — or just describe the job (RTU swap, tree over a house, steel, trusses, signs) and let it derive the numbers.',
    to: '/tools/crane-cost-estimator',
    Icon: ConstructionIcon,
  },
  {
    category: 'Lifting & access',
    title: 'Scaffolding Cost Calculator',
    blurb:
      'Turn a facade into scaffold square footage, rental months, and a cited cost range.',
    to: '/tools/scaffolding-cost-calculator',
    Icon: Layers,
  },
  {
    category: 'Lifting & access',
    title: 'Aerial Lift Cost Calculator',
    blurb:
      'Pick the right scissor or boom lift for the working height and price the rental by day, week, or month.',
    to: '/tools/aerial-lift-cost-calculator',
    Icon: MoveVertical,
  },
  {
    category: 'Drying',
    title: 'Water Mitigation Equipment Calculator',
    blurb:
      'Size air movers and dehumidifiers for a dry-out using the IICRC S500 method.',
    to: '/tools/water-mitigation-calculator',
    Icon: Droplets,
  },
  {
    category: 'Interior',
    title: 'Drywall Repair Calculator',
    blurb:
      'Estimate drywall, tape, texture, paint, and baseboard quantities for a room repair.',
    to: '/tools/drywall-repair-calculator',
    Icon: SquarePen,
  },
  {
    category: 'Debris',
    title: 'Debris & Dumpster Calculator',
    blurb:
      'Turn tear-out quantities into debris weight and the right dumpster size.',
    to: '/tools/dumpster-weight-calculator',
    Icon: Trash2,
  },
];

export default function ToolsIndexPage() {
  useSeo({
    title:
      'Free Restoration Estimating Calculators — Crane, Scaffold, Lift, Drying | Antero Trail',
    description:
      'Free calculators for the equipment costs carrier estimates miss: crane sizing and cost, scaffolding, aerial lifts, water mitigation drying equipment, drywall repair, and dumpster weight. Built on published public rate schedules, with justification narratives you can paste into a supplement.',
    path: '/tools',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Restoration estimating calculators',
        itemListElement: TOOLS.map((t, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: t.title,
          url: `${TOOLS_ORIGIN}${t.to}`,
        })),
      },
    ],
  });

  return (
    <ToolPageShell>
      <Eyebrow>Field tools — free, no account needed</Eyebrow>
      <h1 className="mt-2 font-ui text-4xl font-bold tracking-tight sm:text-5xl">
        Calculators for the costs
        <br className="hidden sm:block" /> carrier estimates miss
      </h1>
      <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
        Cranes, scaffolds, lifts, drying equipment. Xactimate prices the trade
        work; the access and equipment package around it is where supplements
        get contested. Each calculator here sizes the equipment from your
        inputs, prices it from published public rate schedules, and writes a
        short justification narrative you can paste straight into a
        supplement. Planning estimates, not formal quotes.
      </p>

      <div className="mt-10">
        <DimensionDivider label="Six calculators" />
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {TOOLS.map((t) => (
          <Link
            key={t.title}
            to={t.to}
            className="group flex flex-col rounded-lg border border-border bg-card p-5 transition-colors hover:border-amber-500"
          >
            <div className="flex items-center justify-between">
              <t.Icon className="h-6 w-6 text-amber-600 dark:text-amber-500" />
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                {t.category}
              </span>
            </div>
            <h2 className="mt-3 font-ui text-lg font-bold tracking-tight text-foreground">
              {t.title}
            </h2>
            <p className="mt-1 flex-1 text-sm leading-relaxed text-muted-foreground">
              {t.blurb}
            </p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-amber-600 dark:text-amber-500">
              Open calculator
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </Link>
        ))}
      </div>

      <AppPromo
        className="mt-14"
        eyebrow="The engine behind these tools"
        context="In the app, these numbers come out of your field notes automatically — describe the loss once and the estimate, the equipment package, and the justification write themselves."
      />

      <section className="mt-14 max-w-3xl">
        <DimensionDivider label="Why these exist" />
        <h2 className="mb-3 mt-5 font-ui text-2xl font-bold tracking-tight">
          Equipment costs are where estimates get argued
        </h2>
        <div className="space-y-3 text-[15px] leading-relaxed text-muted-foreground">
          <p>
            A crane day, six weeks of scaffold, or a room full of drying
            equipment can move a claim by five figures, and these are exactly
            the items adjusters push back on because the sizing rationale is
            missing. A number without a method is an invitation to cut it.
          </p>
          <p>
            Every calculator here shows its work: the sizing rule it applied,
            the published rate schedule it priced from, and the site factors
            that changed the answer. That is the difference between "crane:
            $4,500" and a package you can defend on the phone with a desk
            adjuster.
          </p>
        </div>
      </section>
    </ToolPageShell>
  );
}
